import { AxiosError } from 'axios';

import {
  ParentLookupImage,
  ParentLookupResponse,
  ParentLookupAdminPackage,
  ParentLookupAdminResponse,
  ParentLookupType,
} from '../types/ParentLookup.type';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './apiClient';

type ApiEnvelope<T> = {
  status: number;
  message: string;
  data: T;
};

export type ParentLookupRequest = {
  lookupType: ParentLookupType;
  query: string;
  captchaToken: string;
};

export type ParentLookupPublicConfig = {
  captchaRequired: boolean;
};

export type ParentLookupSetting = ParentLookupPublicConfig & {
  key: 'PARENT_LOOKUP_ALLOW_MOCK';
  updatedAt: string;
};

type ParentLookupImagesResponse = {
  images: ParentLookupImage[];
};

export type ParentLookupApiError = {
  status: number | null;
  message: string;
  retryAfterSeconds: number | null;
};

export const lookupParentInformation = async (
  body: ParentLookupRequest,
): Promise<ParentLookupResponse> => {
  const response = await apiPost<
    ApiEnvelope<ParentLookupResponse>,
    ParentLookupRequest
  >('/public/parent-lookup', body);
  return response.data;
};

export const lookupParentInformationAdmin = async (body: {
  lookupType: ParentLookupType;
  query: string;
}): Promise<ParentLookupAdminResponse> => {
  const params = new URLSearchParams(body);
  const response = await apiGet<ApiEnvelope<ParentLookupAdminResponse>>(
    `/parent-lookup/admin?${params.toString()}`,
  );
  return response.data;
};

export const updateParentLookupPackage = async (
  registrationId: number,
  payload: { newEndDate: string; remainingSessions?: number },
): Promise<ParentLookupAdminPackage> => {
  const response = await apiPatch<
    ApiEnvelope<ParentLookupAdminPackage>,
    { newEndDate: string; remainingSessions?: number }
  >(`/parent-lookup/admin/registrations/${registrationId}`, payload);
  return response.data;
};

export const getParentLookupPublicConfig =
  async (): Promise<ParentLookupPublicConfig> => {
    const response = await apiGet<ApiEnvelope<ParentLookupPublicConfig>>(
      '/public/parent-lookup/config',
    );
    return response.data;
  };

export const getParentLookupSetting =
  async (): Promise<ParentLookupSetting> => {
    const response = await apiGet<ApiEnvelope<ParentLookupSetting>>(
      '/parent-lookup-setting',
    );
    return response.data;
  };

export const updateParentLookupSetting = async (
  captchaRequired: boolean,
): Promise<ParentLookupSetting> => {
  const response = await apiPatch<
    ApiEnvelope<ParentLookupSetting>,
    { captchaRequired: boolean }
  >('/parent-lookup-setting', { captchaRequired });
  return response.data;
};

export const getParentLookupImages = async (): Promise<ParentLookupImage[]> => {
  const response = await apiGet<ApiEnvelope<ParentLookupImagesResponse>>(
    '/parent-lookup-setting/images',
  );
  return response.data.images;
};

export const uploadParentLookupImage = async (
  posterName: string,
  file: File,
): Promise<ParentLookupImage> => {
  const formData = new FormData();
  formData.append('posterName', posterName);
  formData.append('file', file);
  const response = await apiPost<ApiEnvelope<ParentLookupImage>>(
    '/parent-lookup-setting/images',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
};

export const updateParentLookupImage = async (
  id: number,
  isActive: boolean,
): Promise<ParentLookupImage> => {
  const response = await apiPatch<
    ApiEnvelope<ParentLookupImage>,
    { isActive: boolean }
  >(`/parent-lookup-setting/images/${id}`, { isActive });
  return response.data;
};

export const editParentLookupImage = async (
  id: number,
  posterName: string,
  file?: File | null,
): Promise<ParentLookupImage> => {
  const formData = new FormData();
  formData.append('posterName', posterName);
  if (file) formData.append('file', file);

  const response = await apiPut<ApiEnvelope<ParentLookupImage>, FormData>(
    `/parent-lookup-setting/images/${id}`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return response.data;
};

export const deleteParentLookupImage = async (id: number): Promise<number> => {
  const response = await apiDelete<ApiEnvelope<{ id: number }>>(
    `/parent-lookup-setting/images/${id}`,
  );
  return response.data.id;
};

export const getParentLookupPublicImages = async (): Promise<
  ParentLookupImage[]
> => {
  const response = await apiGet<ApiEnvelope<ParentLookupImagesResponse>>(
    '/public/parent-lookup/images',
  );
  return response.data.images;
};

export const resolveParentLookupImageUrl = (url: string): string =>
  new URL(
    url,
    process.env.REACT_APP_API_BASE_URL || window.location.origin,
  ).toString();

export const getParentLookupApiError = (
  error: unknown,
  fallback: string,
): ParentLookupApiError => {
  const axiosError = error as AxiosError<ApiEnvelope<unknown>>;
  const status = axiosError.response?.status ?? null;
  const responseMessage = axiosError.response?.data?.message;
  const retryHeader =
    axiosError.response?.headers?.['retry-after'] ??
    axiosError.response?.headers?.['Retry-After'];
  const retryAfterSeconds = retryHeader
    ? Math.max(Number(retryHeader), 0) || null
    : null;

  return {
    status,
    message:
      responseMessage || (error instanceof Error ? error.message : fallback),
    retryAfterSeconds,
  };
};
